import { useState, useRef, Fragment } from "react";
import api from "../lib/api";
import { formatEUR } from "../lib/format";
import { Download, Upload, Scale, RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const MESES = ["", "Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

export default function Comparador() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [report, setReport] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showZeroDiffs, setShowZeroDiffs] = useState(false);
  const fileRef = useRef(null);

  const doExport = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/cashflow/export-manual-format?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      a.download = m ? m[1] : `CashFlowProd_${year}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error exportando");
    } finally {
      setExporting(false);
    }
  };

  const doCompare = async (file) => {
    if (!file) return;
    setComparing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/cashflow/compare-manual-excel?year=${year}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(r.data);
      toast.success(`Comparación lista: Δ Fin Año ${formatEUR(r.data.fin_ano.diff)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error comparando");
    } finally {
      setComparing(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) doCompare(f);
    e.target.value = "";
  };

  const projectsWithDiff = report ? report.projects.filter(p => Math.abs(p.diff_net) > 0.01 || p.quincenas_with_diff.length > 0) : [];
  const projectsClean = report ? report.projects.length - projectsWithDiff.length : 0;

  return (
    <div className="space-y-6" data-testid="comparador-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">Cashflow</div>
          <h1 className="font-display text-3xl font-bold">Comparador Excel manual ↔ Prod</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">
            Exporta el estado actual de Prod en el formato de tu Excel y sube tu manual para ver dónde discrepan al detalle (bancos, proyectos por quincena, gastos fijos, saldo final).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-[var(--ink-muted)]">Año</label>
          <select value={year} onChange={e => setYear(parseInt(e.target.value))} className="border rounded px-2 py-1 text-sm" data-testid="cmp-year">
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="border-2 border-black bg-white p-6" data-testid="cmp-export-card">
          <div className="flex items-start gap-3">
            <FileSpreadsheet size={28} className="text-emerald-700 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-lg font-bold">1. Exportar Excel de Prod</div>
              <p className="text-sm text-[var(--ink-muted)] mt-1">
                Descarga un .xlsx con la hoja <code className="bg-gray-100 px-1 rounded">Datos{year}</code> y <code className="bg-gray-100 px-1 rounded">BANCOS</code>, mismo layout que tu manual (24 quincenas, fila CASH FLOW, gastos fijos al pie).
              </p>
              <button onClick={doExport} disabled={exporting} className="mt-4 inline-flex items-center gap-2 bg-black hover:bg-neutral-800 text-white text-sm px-4 py-2 rounded font-semibold disabled:opacity-40" data-testid="cmp-export-btn">
                {exporting ? <RefreshCw size={16} className="animate-spin" /> : <Download size={16} />}
                Descargar CashFlowProd_{year}.xlsx
              </button>
            </div>
          </div>
        </div>

        <div className="border-2 border-black bg-white p-6" data-testid="cmp-compare-card">
          <div className="flex items-start gap-3">
            <Scale size={28} className="text-amber-700 shrink-0" />
            <div className="flex-1">
              <div className="font-display text-lg font-bold">2. Comparar con tu Excel manual</div>
              <p className="text-sm text-[var(--ink-muted)] mt-1">
                Sube tu <code className="bg-gray-100 px-1 rounded">.xlsx</code> manual y te devuelvo el informe de discrepancias: bancos, saldo final, proyectos por quincena y gastos fijos.
              </p>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onFile} data-testid="cmp-file-input" />
              <button onClick={() => fileRef.current?.click()} disabled={comparing} className="mt-4 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded font-semibold disabled:opacity-40" data-testid="cmp-upload-btn">
                {comparing ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                Subir Excel manual y comparar
              </button>
            </div>
          </div>
        </div>
      </div>

      {report && (
        <div className="space-y-6" data-testid="cmp-report">
          {/* Fin Año */}
          <div className="border-2 border-black bg-white p-6">
            <div className="font-display text-lg font-bold mb-3">Cashflow Fin Año</div>
            <div className="grid grid-cols-3 gap-4">
              <SummaryStat label="Excel" value={report.fin_ano.excel} />
              <SummaryStat label="Prod" value={report.fin_ano.prod} />
              <SummaryStat label="Δ (Prod - Excel)" value={report.fin_ano.diff} highlight />
            </div>
          </div>

          {/* Bancos */}
          <div className="border-2 border-black bg-white p-6" data-testid="cmp-banks-section">
            <div className="font-display text-lg font-bold mb-3">Bancos</div>
            <div className="text-sm">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2">Banco</th>
                    <th className="text-right py-2">Excel</th>
                    <th className="text-right py-2">Prod</th>
                    <th className="text-right py-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {report.banks.rows.map(b => (
                    <tr key={b.name} className={`border-b ${Math.abs(b.diff) > 0.5 ? "bg-red-50" : ""}`}>
                      <td className="py-1.5">{b.name}</td>
                      <td className="text-right font-mono-num">{formatEUR(b.excel)}</td>
                      <td className="text-right font-mono-num">{formatEUR(b.prod)}</td>
                      <td className={`text-right font-mono-num font-semibold ${b.diff > 0.5 ? "text-emerald-700" : b.diff < -0.5 ? "text-red-700" : "text-gray-400"}`}>{formatEUR(b.diff)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-black font-bold">
                    <td className="py-2">TOTAL</td>
                    <td className="text-right font-mono-num">{formatEUR(report.banks.excel_total)}</td>
                    <td className="text-right font-mono-num">{formatEUR(report.banks.prod_total)}</td>
                    <td className="text-right font-mono-num">{formatEUR(report.banks.diff)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Columnas por quincena */}
          <div className="border-2 border-black bg-white p-6" data-testid="cmp-columns-section">
            <div className="font-display text-lg font-bold mb-3">Movimientos por quincena</div>
            <div className="text-sm overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2">Q</th>
                    <th className="text-right py-2">Excel</th>
                    <th className="text-right py-2">Prod</th>
                    <th className="text-right py-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {report.columns.filter(c => showZeroDiffs || Math.abs(c.diff) > 0.5 || Math.abs(c.excel_movement) > 0.5 || Math.abs(c.prod_movement) > 0.5).map(c => (
                    <tr key={`${c.month}-${c.quincena}`} className={`border-b ${Math.abs(c.diff) > 0.5 ? "bg-amber-50" : ""}`}>
                      <td className="py-1">{MESES[c.month]}-Q{c.quincena}</td>
                      <td className="text-right font-mono-num">{formatEUR(c.excel_movement)}</td>
                      <td className="text-right font-mono-num">{formatEUR(c.prod_movement)}</td>
                      <td className={`text-right font-mono-num font-semibold ${c.diff > 0.5 ? "text-emerald-700" : c.diff < -0.5 ? "text-red-700" : "text-gray-400"}`}>{formatEUR(c.diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fijos */}
          <div className="border-2 border-black bg-white p-6" data-testid="cmp-fixed-section">
            <div className="font-display text-lg font-bold mb-3">Gastos fijos por sección</div>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left py-2">Sección</th>
                  <th className="text-right py-2">Excel</th>
                  <th className="text-right py-2">Prod</th>
                  <th className="text-right py-2">Δ</th>
                </tr>
              </thead>
              <tbody>
                {report.fixed.map(f => (
                  <tr key={f.section} className={`border-b ${Math.abs(f.diff) > 0.5 ? "bg-red-50" : ""}`}>
                    <td className="py-1.5">{f.section}</td>
                    <td className="text-right font-mono-num">{formatEUR(f.excel_total)}</td>
                    <td className="text-right font-mono-num">{formatEUR(f.prod_total)}</td>
                    <td className={`text-right font-mono-num font-semibold ${f.diff > 0.5 ? "text-emerald-700" : f.diff < -0.5 ? "text-red-700" : "text-gray-400"}`}>{formatEUR(f.diff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Proyectos */}
          <div className="border-2 border-black bg-white p-6" data-testid="cmp-projects-section">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-bold">Proyectos con discrepancias</div>
              <div className="text-xs text-[var(--ink-muted)]">
                {projectsWithDiff.length} con diferencias · {projectsClean} idénticos
                <span className="mx-2">·</span>
                Excel: {report.counts.projects_excel} · Prod: {report.counts.projects_prod} · Ambos: {report.counts.projects_both}
              </div>
            </div>
            <div className="text-sm">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    <th className="text-left py-2 w-14">Código</th>
                    <th className="text-left py-2">Nombre</th>
                    <th className="text-center py-2 w-24">Origen</th>
                    <th className="text-right py-2 w-32">Δ Net anual</th>
                    <th className="text-right py-2 w-24">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {projectsWithDiff.sort((a, b) => Math.abs(b.diff_net) - Math.abs(a.diff_net)).map(p => {
                    const key = `p_${p.code}`;
                    const isOpen = expanded[key];
                    return (
                      <Fragment key={key}>
                        <tr className={`border-b hover:bg-gray-50 ${Math.abs(p.diff_net) > 1000 ? "bg-red-50" : ""}`}>
                          <td className="py-1.5 font-mono-num">{p.code}</td>
                          <td className="py-1.5">{p.name}</td>
                          <td className="text-center text-xs">
                            {p.origin === "both" ? <span className="text-gray-500">ambos</span>
                              : p.origin === "excel_only" ? <span className="text-blue-700">sólo Excel</span>
                              : <span className="text-orange-700">sólo Prod</span>}
                          </td>
                          <td className={`text-right font-mono-num font-semibold ${p.diff_net > 0.5 ? "text-emerald-700" : p.diff_net < -0.5 ? "text-red-700" : ""}`}>{formatEUR(p.diff_net)}</td>
                          <td className="text-right">
                            {p.quincenas_with_diff.length > 0 && (
                              <button className="text-xs underline" onClick={() => setExpanded(x => ({ ...x, [key]: !x[key] }))} data-testid={`cmp-p-${p.code}-toggle`}>
                                {isOpen ? <><ChevronDown size={12} className="inline" /> ocultar</> : <><ChevronRight size={12} className="inline" /> {p.quincenas_with_diff.length} Q</>}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={5} className="bg-neutral-50 p-3">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b">
                                    <th className="text-left py-1">Q</th>
                                    <th className="text-right py-1">Excel Ing</th>
                                    <th className="text-right py-1">Prod Ing</th>
                                    <th className="text-right py-1">Δ Ing</th>
                                    <th className="text-right py-1">Excel Gas</th>
                                    <th className="text-right py-1">Prod Gas</th>
                                    <th className="text-right py-1">Δ Gas</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.quincenas_with_diff.map(q => (
                                    <tr key={`${q.month}-${q.quincena}`}>
                                      <td className="py-0.5">{MESES[q.month]}-Q{q.quincena}</td>
                                      <td className="text-right font-mono-num">{formatEUR(q.excel_ing)}</td>
                                      <td className="text-right font-mono-num">{formatEUR(q.prod_ing)}</td>
                                      <td className={`text-right font-mono-num font-semibold ${q.ing_diff > 0.5 ? "text-emerald-700" : q.ing_diff < -0.5 ? "text-red-700" : ""}`}>{formatEUR(q.ing_diff)}</td>
                                      <td className="text-right font-mono-num">{formatEUR(q.excel_gas)}</td>
                                      <td className="text-right font-mono-num">{formatEUR(q.prod_gas)}</td>
                                      <td className={`text-right font-mono-num font-semibold ${q.gas_diff > 0.5 ? "text-emerald-700" : q.gas_diff < -0.5 ? "text-red-700" : ""}`}>{formatEUR(q.gas_diff)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {projectsWithDiff.length === 0 && (
                <div className="text-center py-6 text-sm text-emerald-700">✓ Todos los proyectos coinciden con Excel</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, highlight }) {
  const cls = highlight
    ? (value > 0 ? "text-emerald-700" : value < 0 ? "text-red-700" : "")
    : "";
  return (
    <div className={`border p-4 ${highlight ? "border-black bg-neutral-50" : ""}`}>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)]">{label}</div>
      <div className={`text-xl font-bold font-mono-num mt-1 ${cls}`}>{formatEUR(value)}</div>
    </div>
  );
}
